import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, QueryList, ViewChildren, inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { Router, RouterModule } from '@angular/router';
import { of, forkJoin, map, switchMap, firstValueFrom } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';

import { TrilhaService, ModuloService, AulaService, AulaProgressService, BannerService } from 'app/shared/services/edduca-imob';
import { BannerHomeResponse } from 'app/shared/models/ei-models';
import { ConquistaService } from 'app/shared/services/edduca-imob/conquista/conquista.service';
import { RankingService } from 'app/shared/services/edduca-imob/ranking/ranking.service';
import { HomeService } from 'app/shared/services/edduca-imob/home/home.service';

import { SearchModuloRequest, TrilhaModuloResponse } from 'app/shared/models/ei-models';
import { AuthService } from 'app/shared/services/core';
import { PerfilEnum } from 'app/shared/models/core/security/perfil.enum';
import { DateTime } from 'luxon';
import { useMenuTracking } from 'app/shared/hooks';

import {
  HomeAdmImobiliariaResponse,
  HomeUsuarioPlano1Response,
  HomeUsuarioPlano2Response,
  HomeAdmSistemaResponse,
  HomeParceiroResponse,
  ContinuarAssistindoHomeResponse
} from 'app/shared/services/edduca-imob/home/home.response';

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [CommonModule, RouterModule, MatIconModule],
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss']
})
export class HomeComponent implements OnInit, OnDestroy, AfterViewInit {

  capa = 'https://s3.us-east-1.amazonaws.com/images.edimb/banners/home/capa%2Bhome.png';

  // Banners customizados (carrossel da home)
  banners: BannerHomeResponse[] = [];
  bannerAtualIndex = 0;
  private bannerTimer: any = null;

  // Dados por perfil
  homeAdmImobiliaria: HomeAdmImobiliariaResponse | null = null;
  homeUsuarioPlano1: HomeUsuarioPlano1Response | null = null;
  homeUsuarioPlano2: HomeUsuarioPlano2Response | null = null;
  homeAdmSistema: HomeAdmSistemaResponse | null = null;
  homeParceiro: HomeParceiroResponse | null = null;

  // Controle de perfil
  currentUserProfile: number;
  currentUserPlanoId: number | null;
  private userId!: number;
  
  loading = true;

  // Continue assistindo (módulos com progresso 1..99%) - mantido para fallback
  continuar: any[] = [];

  // Círculo de progresso
  ring = { size: 32, r: 13, sw: 3, get c() { return 2 * Math.PI * this.r; } };

  @ViewChildren('carouselRef') carousels!: QueryList<ElementRef<HTMLDivElement>>;
  overflows: boolean[] = [];
  canLeft: boolean[] = [];
  canRight: boolean[] = [];

  private menuTracking = useMenuTracking();

  constructor(
    private auth: AuthService,
    private trilhas: TrilhaService,
    private modulos: ModuloService,
    private aulas: AulaService,
    private progress: AulaProgressService,
    private conquistas: ConquistaService,
    private ranking: RankingService,
    private homeService: HomeService,
    private bannerService: BannerService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    const currentUser = this.auth.getUserInToken();
    this.currentUserProfile = currentUser.perfilId;
    this.currentUserPlanoId = currentUser.planoId;
    this.userId = currentUser.userId;

    this.carregarBanners();
  }

  ngOnDestroy(): void {
    this.pararCarrossel();
  }

  private carregarBanners(): void {
    this.bannerService.listarParaHome().subscribe({
      next: (res) => {
        this.banners = res ?? [];
        this.bannerAtualIndex = 0;
        if (this.banners.length > 1) this.iniciarCarrossel();
      },
      error: () => this.banners = []
    });
  }

  private iniciarCarrossel(): void {
    this.pararCarrossel();
    this.bannerTimer = setInterval(() => {
      this.bannerAtualIndex = (this.bannerAtualIndex + 1) % this.banners.length;
      this.cdr.markForCheck?.();
    }, 5000);
  }

  private pararCarrossel(): void {
    if (this.bannerTimer) {
      clearInterval(this.bannerTimer);
      this.bannerTimer = null;
    }
  }

  irParaBanner(index: number): void {
    if (index < 0 || index >= this.banners.length) return;
    this.bannerAtualIndex = index;
    if (this.banners.length > 1) this.iniciarCarrossel();
  }

  abrirLinkBanner(banner: BannerHomeResponse): void {
    // VideoUrl tem prioridade: abre modal com player Panda Video
    if (banner.videoUrl?.trim()) {
      import('./banner-video-dialog/banner-video-dialog.component').then(m => {
        this.dialog.open(m.BannerVideoDialogComponent, {
          panelClass: 'banner-video-dialog-panel',
          maxWidth: '1100px',
          width: '92vw',
          autoFocus: false,
          data: { videoUrl: banner.videoUrl!.trim(), titulo: banner.nome }
        });
      });
      return;
    }

    // Senão, comportamento atual: abre o link externo em nova aba
    if (!banner.link) return;
    let url = banner.link.trim();
    if (!/^https?:\/\//i.test(url)) {
      url = 'https://' + url;
    }
    window.open(url, '_blank', 'noopener');
  }

  /** True quando o banner é clicável (tem vídeo OU link). Usado pra cursor/pointer. */
  bannerClicavel(banner: BannerHomeResponse): boolean {
    return !!(banner.videoUrl?.trim() || banner.link?.trim());
  }

  async ngAfterViewInit() {
    // Registrar acesso ao menu home para gamificação
    this.menuTracking.registrarAcessoMenu('home');
    
    await this.loadHomeData();
    await this.carregarContinuarAssistindo();
    setTimeout(() => this.computeAllOverflow());
  }

  // ============ GETTERS DE PERFIL ============

  get isAdmImobiliaria(): boolean {
    return this.currentUserProfile === PerfilEnum.ADM_IMOBILIARIA;
  }

  get isUsuarioImobiliaria(): boolean {
    return this.currentUserProfile === PerfilEnum.USUARIO_IMOBILIARIA;
  }

  get isUsuarioPlano1(): boolean {
    return this.isUsuarioImobiliaria && this.currentUserPlanoId === 1;
  }

  get isUsuarioPlano2Plus(): boolean {
    return this.isUsuarioImobiliaria && this.currentUserPlanoId !== null && this.currentUserPlanoId >= 2;
  }

  get isAdmSistema(): boolean {
    return this.currentUserProfile === PerfilEnum.ADM_SISTEMA;
  }

  get isParceiro(): boolean {
    return this.currentUserProfile === PerfilEnum.PARCEIRO;
  }

  // ============ DESCRIÇÕES POR PERFIL ============


  get performanceTitle(): string {
    if (this.isAdmSistema) return 'Visão Geral do Sistema';
    if (this.isParceiro) return 'Métricas do Parceiro';
    if (this.isAdmImobiliaria) return 'Performance da Equipe';
    return 'Sua performance';
  }

  // ============ CARREGAMENTO DE DADOS ============

  private async loadHomeData(): Promise<void> {
    this.loading = true;
    this.clearHomeData();

    try {
      if (this.isAdmImobiliaria) {
        await this.loadHomeAdmImobiliaria();
      } else if (this.isUsuarioImobiliaria) {
        await this.loadHomeUsuario();
      } else if (this.isAdmSistema) {
        await this.loadHomeAdmSistema();
      } else if (this.isParceiro) {
        await this.loadHomeParceiro();
      }
    } catch (error) {
      console.error('Erro ao carregar dados da Home:', error);
    } finally {
      this.loading = false;
      this.cdr.markForCheck();
    }
  }

  private async loadHomeAdmImobiliaria(): Promise<void> {
    try {
      this.homeAdmImobiliaria = await firstValueFrom(this.homeService.getHomeAdmImobiliaria());
    } catch (error) {
      console.error('Erro ao carregar home ADM_IMOBILIARIA:', error);
    }
  }

  private async loadHomeUsuario(): Promise<void> {
    try {
      const data = await firstValueFrom(this.homeService.getHomeUsuario());
      // Verifica se tem badges para determinar se é plano 2+
      if ('badgesConquistadas' in data) {
        this.homeUsuarioPlano2 = data as HomeUsuarioPlano2Response;
      } else {
        this.homeUsuarioPlano1 = data as HomeUsuarioPlano1Response;
      }
    } catch (error) {
      console.error('Erro ao carregar home USUARIO:', error);
    }
  }

  private async loadHomeAdmSistema(): Promise<void> {
    try {
      this.homeAdmSistema = await firstValueFrom(this.homeService.getHomeAdmSistema());
    } catch (error) {
      console.error('Erro ao carregar home ADM_SISTEMA:', error);
    }
  }

  private async loadHomeParceiro(): Promise<void> {
    try {
      this.homeParceiro = await firstValueFrom(this.homeService.getHomeParceiro());
    } catch (error) {
      console.error('Erro ao carregar home PARCEIRO:', error);
    }
  }

  private clearHomeData(): void {
    this.homeAdmImobiliaria = null;
    this.homeUsuarioPlano1 = null;
    this.homeUsuarioPlano2 = null;
    this.homeAdmSistema = null;
    this.homeParceiro = null;
  }

  // ============ HELPERS DE FORMATAÇÃO ============

  formatTempoAssistido(): string {
    const tempo = this.homeUsuarioPlano1?.tempoAssistido || this.homeUsuarioPlano2?.tempoAssistido;
    if (!tempo) return '0h';
    const h = tempo.horas;
    const m = tempo.minutos;
    return h ? `${h}h ${m}m` : `${m}m`;
  }

  getCrescimentoIcon(variacao: 'positivo' | 'negativo' | 'neutro'): string {
    switch (variacao) {
      case 'positivo': return 'trending_up';
      case 'negativo': return 'trending_down';
      default: return 'trending_flat';
    }
  }

  getCrescimentoColorClass(variacao: 'positivo' | 'negativo' | 'neutro'): string {
    switch (variacao) {
      case 'positivo': return 'text-green-500';
      case 'negativo': return 'text-red-500';
      default: return 'text-white/50';
    }
  }

  // ============ NAVEGAÇÃO ============

  navegarParaRanking(): void {
    this.router.navigate(['/ranking']);
  }

  navegarParaBadges(): void {
    this.router.navigate(['/badges']);
  }

  navegarParaCertificados(): void {
    this.router.navigate(['/certificados']);
  }

  navegarParaGestao(): void {
    this.router.navigate(['/gestao']);
  }

  // ========== CONTINUE ASSISTINDO ==========
  private async carregarContinuarAssistindo() {
    const trilhas = await firstValueFrom(this.trilhas.listar());
    if (!trilhas?.length) { this.continuar = []; return; }

    const blocos$ = trilhas.map(t => {
      const req: SearchModuloRequest = { trilhaId: t.id, nome: '' };
      return this.modulos.listarModulos(req).pipe(
        map(mods => mods.sort((a, b) => a.ordem - b.ordem).map(m => ({ ...m, progresso: 0 })))
      );
    });
    const blocos = await firstValueFrom(forkJoin(blocos$));

    const calls: any[] = [];
    for (const mods of blocos) {
      for (const m of mods as any[]) {
        calls.push(
          this.aulas.listarPorModulo(m.id).pipe(
            switchMap(aulas => {
              const ids = (aulas || []).map(a => a.id);
              if (!ids.length) { m.progresso = 0; return of(null); }
              return this.progress.getMany(ids).pipe(
                map(pl => {
                  const concluidas = pl.filter(p => p.concluida).length;
                  const pct = Math.round((concluidas / ids.length) * 100);
                  m.progresso = Number.isFinite(pct) ? pct : 0;
                  (m as any).__aulas = aulas?.sort?.((a: any, b: any) => (a.ordem ?? 0) - (b.ordem ?? 0)) ?? [];
                  (m as any).__progressList = pl;
                })
              );
            })
          )
        );
      }
    }
    if (calls.length) await firstValueFrom(forkJoin(calls));

    const todos = blocos.flat();
    this.continuar = todos.filter((m: any) => m.progresso > 0 && m.progresso < 100);
    this.cdr.markForCheck();
  }

  // Vai para a primeira aula não concluída; fallback: módulo
  openEmAndamento(mod: any) {
    try {
      const aulas = mod?.__aulas || [];
      const pl = mod?.__progressList || [];
      const done = new Set(pl.filter((p: any) => p.concluida).map((p: any) => p.aulaId));
      const alvo = aulas.find((a: any) => !done.has(a.id)) || aulas[0];
      if (alvo?.id) { this.router.navigate(['/aula', alvo.id]); return; }
    } catch {}
    this.router.navigate(['/modulo', mod.id]);
  }

  // ========== Carrossel util ==========
  private computeAllOverflow() { this.carousels?.forEach((_, i) => this.computeOverflowFor(i)); }
  computeOverflowFor(i: number) {
    const el = this.carousels.get(i)?.nativeElement; if (!el) return;
    const hasOverflow = el.scrollWidth > el.clientWidth + 1;
    this.overflows[i] = hasOverflow;
    this.canLeft[i] = hasOverflow && el.scrollLeft > 0;
    this.canRight[i] = hasOverflow && (el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }
  scrollCarousel(i: number, dir: 'left' | 'right') {
    const el = this.carousels.get(i)?.nativeElement; if (!el) return;
    el.scrollBy({ left: dir === 'left' ? -340 : 340, behavior: 'smooth' });
    setTimeout(() => this.computeOverflowFor(i), 360);
  }

  // Círculo de progresso
  dashOffset(pct: number | undefined | null): number {
    const p = Math.max(0, Math.min(100, Number(pct) || 0));
    return this.ring.c * (1 - p / 100);
  }
  getProgressColor(pct: number): string {
    if (pct >= 100) return '#22c55e'; // verde
    if (pct > 0)   return '#E4811E'; // laranja
    return '#9CA3AF';                // cinza
  }
}
